import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, query, orderBy, getDocs } from 'firebase/firestore';
import { app } from '../../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { format, subWeeks, subMonths, startOfDay } from 'date-fns';
import styles from './Summary.module.css';

const auth = getAuth(app);
const db = getFirestore(app);

// Status Card Component - Mobile optimized
const StatusCard = ({ status, count, onClick, isSelected = false }) => {
  // Status styles matching your existing code
  const statusStyles = {
    'delivered': { 
      bgClass: 'bg-green-100', 
      textClass: 'text-green-800',
      countClass: 'text-green-600'
    },
    'in-transit': { 
      bgClass: 'bg-blue-100', 
      textClass: 'text-blue-800',
      countClass: 'text-blue-600'
    },
    'paid': { 
      bgClass: 'bg-purple-100', 
      textClass: 'text-purple-800',
      countClass: 'text-purple-600'
    },
    'pending': { 
      bgClass: 'bg-gray-100', 
      textClass: 'text-gray-800',
      countClass: 'text-gray-600'
    },
    'delayed': { 
      bgClass: 'bg-yellow-100', 
      textClass: 'text-yellow-800',
      countClass: 'text-yellow-600'
    },
    'returned': { 
      bgClass: 'bg-red-100', 
      textClass: 'text-red-800',
      countClass: 'text-red-600'
    },
    // Default style for any new statuses
    'default': { 
      bgClass: 'bg-gray-100', 
      textClass: 'text-gray-800',
      countClass: 'text-gray-600'
    }
  };

  // Get appropriate counter text color and background based on selection
  const getCounterStyle = (status) => {
    if (!status) return statusStyles.default.countClass;
    
    const style = statusStyles[status.toLowerCase()] || statusStyles.default;
    
    return {
      textClass: style.countClass,
      bgClass: isSelected ? 'ring-2 ring-indigo-500 bg-indigo-50' : ''
    };
  };

  // Format status for display - Mobile optimized
  const formatStatus = (status) => {
    if (!status) return 'Pending';
    
    // Shorter labels on mobile
    const mobileLabels = {
      'total': 'Total',
      'in-transit': 'Transit',
      'delivered': 'Delivered',
      'delayed': 'Delayed',
      'pending': 'Pending',
      'returned': 'Returned',
      'paid': 'Paid'
    };
    
    return mobileLabels[status.toLowerCase()] || status
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const counterStyle = getCounterStyle(status);

  return (
    <button
      onClick={() => onClick(status)}
      className={`bg-white rounded-lg shadow p-3 md:p-4 text-left transition-all hover:shadow-md active:scale-95 ${counterStyle.bgClass}`}
    >
      <div className="text-xs md:text-sm text-gray-500 mb-1 truncate">
        {status === 'total' ? 'Total' : formatStatus(status)}
      </div>
      <div className={`text-xl md:text-2xl font-bold ${counterStyle.textClass}`}>{count}</div>
    </button>
  );
};

export default function SummaryPage() {
  const [packages, setPackages] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [user] = useAuthState(auth);
  const [timeFilter, setTimeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // New status filter
  const [searchQuery, setSearchQuery] = useState(''); // New search query
  const [sortBy, setSortBy] = useState('updatedAt'); // New sort option
  const [sortOrder, setSortOrder] = useState('desc'); // New sort order
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const navigate = useNavigate();

  // Status badge and counter styles
  const statusStyles = {
    'delivered': { 
      bgClass: 'bg-green-100', 
      textClass: 'text-green-800',
      countClass: 'text-green-600'
    },
    'in-transit': { 
      bgClass: 'bg-blue-100', 
      textClass: 'text-blue-800',
      countClass: 'text-blue-600'
    },
    'paid': { 
      bgClass: 'bg-purple-100', 
      textClass: 'text-purple-800',
      countClass: 'text-purple-600'
    },
    'pending': { 
      bgClass: 'bg-gray-100', 
      textClass: 'text-gray-800',
      countClass: 'text-gray-600'
    },
    'delayed': { 
      bgClass: 'bg-yellow-100', 
      textClass: 'text-yellow-800',
      countClass: 'text-yellow-600'
    },
    'returned': { 
      bgClass: 'bg-red-100', 
      textClass: 'text-red-800',
      countClass: 'text-red-600'
    },
    // Default style for any new statuses
    'default': { 
      bgClass: 'bg-gray-100', 
      textClass: 'text-gray-800',
      countClass: 'text-gray-600'
    }
  };

  // Fetch all packages
  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }

    const fetchPackages = async () => {
      try {
        setLoading(true);
        // Get all packages sorted by updatedAt
        const packagesQuery = query(collection(db, 'packages'), orderBy('updatedAt', 'desc'));
        const querySnapshot = await getDocs(packagesQuery);
        const pkgList = querySnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(pkg => {
            const status = (pkg.status || 'pending').toLowerCase();
            return status !== 'paid' && status !== 'returned';
          });
        setPackages(pkgList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching packages:", error);
        setLoading(false);
      }
    };
    
    fetchPackages();
  }, [user, navigate]);

  // Apply all filters and sorting
  useEffect(() => {
    if (packages.length === 0) {
      setFilteredPackages([]);
      return;
    }

    let filtered = [...packages];

    // 1. Apply time filter
    if (timeFilter !== 'all') {
      const now = new Date();
      let cutoffDate;

      switch (timeFilter) {
        case 'daily':
          cutoffDate = startOfDay(now);
          break;
        case 'weekly':
          cutoffDate = subWeeks(now, 1);
          break;
        case 'monthly':
          cutoffDate = subMonths(now, 1);
          break;
        default:
          cutoffDate = null;
      }

      if (cutoffDate) {
        filtered = filtered.filter(pkg => {
          const timestamp = pkg.updatedAt || pkg.createdAt;
          if (!timestamp) return false;
          
          const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
          return date >= cutoffDate;
        });
      }
    }

    // 2. Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(pkg => {
        const status = (pkg.status || 'pending').toLowerCase();
        return status === statusFilter.toLowerCase();
      });
    }

    // 3. Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(pkg => {
        return (
          pkg.recipient?.toLowerCase().includes(query) ||
          pkg.trackingNumber?.toLowerCase().includes(query) ||
          pkg.status?.toLowerCase().includes(query) ||
          pkg.destination?.toLowerCase().includes(query) ||
          pkg.description?.toLowerCase().includes(query)
        );
      });
    }

    // 4. Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'recipient':
          aValue = a.recipient || '';
          bValue = b.recipient || '';
          break;
        case 'trackingNumber':
          aValue = a.trackingNumber || '';
          bValue = b.trackingNumber || '';
          break;
        case 'status':
          aValue = a.status || 'pending';
          bValue = b.status || 'pending';
          break;
        case 'updatedAt':
        default:
          aValue = a.updatedAt || a.createdAt;
          bValue = b.updatedAt || b.createdAt;
          // Convert to timestamp for comparison
          if (aValue?.toDate) aValue = aValue.toDate().getTime();
          else if (aValue) aValue = new Date(aValue).getTime();
          else aValue = 0;
          
          if (bValue?.toDate) bValue = bValue.toDate().getTime();
          else if (bValue) bValue = new Date(bValue).getTime();
          else bValue = 0;
          break;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredPackages(filtered);
  }, [timeFilter, statusFilter, searchQuery, sortBy, sortOrder, packages]);
  
  // Calculate counts for each status (based on time filter only, not status filter)
  useEffect(() => {
    if (packages.length === 0) {
      setStatusCounts({});
      return;
    }

    // Apply only time filter for status counts (not status or search filters)
    let timeFilteredPackages = [...packages];
    
    if (timeFilter !== 'all') {
      const now = new Date();
      let cutoffDate;

      switch (timeFilter) {
        case 'daily':
          cutoffDate = startOfDay(now);
          break;
        case 'weekly':
          cutoffDate = subWeeks(now, 1);
          break;
        case 'monthly':
          cutoffDate = subMonths(now, 1);
          break;
        default:
          cutoffDate = null;
      }

      if (cutoffDate) {
        timeFilteredPackages = timeFilteredPackages.filter(pkg => {
          const timestamp = pkg.updatedAt || pkg.createdAt;
          if (!timestamp) return false;
          
          const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
          return date >= cutoffDate;
        });
      }
    }

    // Get counts for each status
    const counts = {};
    counts['total'] = timeFilteredPackages.length;
    
    timeFilteredPackages.forEach(pkg => {
      const status = (pkg.status || 'pending').toLowerCase();
      
      if (counts[status]) {
        counts[status]++;
      } else {
        counts[status] = 1;
      }
    });
    
    setStatusCounts(counts);
  }, [timeFilter, packages]);

  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return format(date, 'MMM d, yyyy h:mm a');
    } catch (error) {
      console.error("Error formatting date:", error);
      return 'Invalid Date';
    }
  };

  // Get appropriate status badge style
  const getStatusStyle = (status) => {
    if (!status) return `${statusStyles.default.bgClass} ${statusStyles.default.textClass}`;
    
    const style = statusStyles[status.toLowerCase()] || statusStyles.default;
    return `${style.bgClass} ${style.textClass}`;
  };

  // Format status for display
  const formatStatus = (status) => {
    if (!status) return 'Pending';
    
    return status
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Handle status badge click
  const handleStatusClick = (status) => {
    if (status === 'total') {
      setStatusFilter('all');
    } else {
      setStatusFilter(statusFilter === status ? 'all' : status);
    }
  };

  // Handle sort change
  const handleSort = (newSortBy) => {
    if (sortBy === newSortBy) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort column with default desc order
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
  };

  // Get sort icon
  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    return sortOrder === 'asc' ? (
      <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
      </svg>
    );
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredPackages.length / pageSize);
  const paginatedPackages = filteredPackages.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredPackages, pageSize]);

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      {/* Header - Mobile optimized */}
      <div className="mb-4 md:mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Package Summary</h1>
        <p className="text-sm text-gray-600 mt-1 hidden md:block">
          View and manage all your packages
        </p>
      </div>
      
      {/* Search Bar - Mobile optimized */}
      <div className="bg-white shadow rounded-lg p-3 md:p-4 mb-4 md:mb-6">
        <div className={styles.mobileSearchWrapper}>
          <input
            type="text"
            placeholder="Search packages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 ${styles.mobileSearchInput}`}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="bg-gray-500 hover:bg-gray-600 text-white px-3 md:px-4 py-2 rounded transition-colors flex-shrink-0"
            >
              <span className="hidden sm:inline">Clear</span>
              <span className="sm:hidden">×</span>
            </button>
          )}
        </div>
      </div>
      
      {/* Time Filter Tabs - Mobile optimized */}
      <div className="bg-white shadow rounded-lg p-3 mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <span className="text-xs md:text-sm font-medium text-gray-500 flex-shrink-0">Time Period:</span>
          
          <div className="grid grid-cols-4 sm:flex sm:flex-wrap gap-2">
            {['daily', 'weekly', 'monthly', 'all'].map(period => (
              <button
                key={period}
                onClick={() => setTimeFilter(period)}
                className={`px-2 md:px-3 py-1.5 md:py-1 rounded-full text-xs md:text-sm font-medium transition-colors
                  ${timeFilter === period 
                    ? 'bg-indigo-500 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Status Cards - Mobile optimized grid */}
      <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6 ${styles.mobileStatusGrid}`}>
        {/* Total count always first */}
        <StatusCard
          status="total"
          count={statusCounts.total || 0}
          onClick={handleStatusClick}
          isSelected={statusFilter === 'all'}
        />
        
        {/* Static status cards */}
        <StatusCard
          status="delivered"
          count={statusCounts.delivered || 0}
          onClick={handleStatusClick}
          isSelected={statusFilter === 'delivered'}
        />
        
        <StatusCard
          status="in-transit"
          count={statusCounts['in-transit'] || 0}
          onClick={handleStatusClick}
          isSelected={statusFilter === 'in-transit'}
        />
        
        <StatusCard
          status="delayed"
          count={statusCounts.delayed || 0}
          onClick={handleStatusClick}
          isSelected={statusFilter === 'delayed'}
        />
      </div>

      {/* Active filters display - Mobile optimized */}
      {(statusFilter !== 'all' || searchQuery.trim()) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 md:p-3 mb-4 md:mb-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs md:text-sm font-medium text-blue-800">Active Filters:</span>
            
            {statusFilter !== 'all' && (
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1">
                <span className="hidden sm:inline">Status: </span>
                {formatStatus(statusFilter)}
                <button
                  onClick={() => setStatusFilter('all')}
                  className="text-blue-600 hover:text-blue-800 font-bold"
                >
                  ×
                </button>
              </span>
            )}
            
            {searchQuery.trim() && (
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1">
                <span className="hidden sm:inline">Search: </span>
                <span className="truncate max-w-[120px] sm:max-w-none">"{searchQuery}"</span>
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-blue-600 hover:text-blue-800 font-bold flex-shrink-0"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Mobile Sort Control - Only visible on mobile */}
      <div className="md:hidden bg-white shadow rounded-lg p-3 mb-4">
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-gray-600 flex-shrink-0">Sort by:</label>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="flex-1 border rounded px-2 py-1.5 text-xs bg-white"
          >
            <option value="updatedAt">Date Updated</option>
            <option value="recipient">Recipient</option>
            <option value="destination">Destination</option>
            <option value="trackingNumber">Tracking #</option>
            <option value="status">Status</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="px-3 py-1.5 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
            aria-label={`Sort ${sortOrder === 'asc' ? 'descending' : 'ascending'}`}
          >
            {sortOrder === 'asc' ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Packages per page selector and pagination controls - Mobile optimized */}
      <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 ${styles.mobilePagination}`}>
        <div className="flex items-center gap-2">
          <label className="text-xs md:text-sm font-medium text-gray-600 flex-shrink-0">Per page:</label>
          <select
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}
            className="border rounded px-2 py-1.5 text-xs md:text-sm bg-white"
          >
            {[20, 50, 100].map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>
        <div className={`flex items-center justify-between sm:justify-end gap-3 ${styles.mobilePageControls}`}>
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 md:px-4 py-1.5 md:py-2 rounded bg-gray-200 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs md:text-sm font-medium hover:bg-gray-300 transition-colors"
          >
            <span className="hidden sm:inline">Previous</span>
            <span className="sm:hidden">‹ Prev</span>
          </button>
          <span className="text-xs md:text-sm font-medium text-gray-700">
            Page {currentPage} of {totalPages || 1}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages || totalPages === 0}
            className="px-3 md:px-4 py-1.5 md:py-2 rounded bg-gray-200 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs md:text-sm font-medium hover:bg-gray-300 transition-colors"
          >
            <span className="hidden sm:inline">Next</span>
            <span className="sm:hidden">Next ›</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col justify-center items-center p-8 md:p-12 bg-white rounded-lg shadow">
          <div className="animate-pulse flex space-x-2 mb-3">
            <div className="h-2 w-2 bg-indigo-500 rounded-full"></div>
            <div className="h-2 w-2 bg-indigo-500 rounded-full"></div>
            <div className="h-2 w-2 bg-indigo-500 rounded-full"></div>
          </div>
          <p className="text-gray-600 text-sm md:text-base">Loading packages...</p>
        </div>
      ) : paginatedPackages.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-6 md:p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <p className="text-gray-500 text-sm md:text-base mb-2">
            {packages.length === 0 
              ? "No packages found" 
              : "No packages match your filters"}
          </p>
          <p className="text-gray-400 text-xs md:text-sm mb-4">
            {packages.length === 0 
              ? "Add your first package to get started!" 
              : "Try adjusting your filters to see more results"}
          </p>
          {(statusFilter !== 'all' || searchQuery.trim()) && (
            <div className="mt-4">
              <button
                onClick={() => {
                  setStatusFilter('all');
                  setSearchQuery('');
                }}
                className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className={`bg-white shadow rounded-lg overflow-hidden ${styles.desktopTableView}`}>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('updatedAt')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Date</span>
                        {getSortIcon('updatedAt')}
                      </div>
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('recipient')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Recipient</span>
                        {getSortIcon('recipient')}
                      </div>
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('destination')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Destination</span>
                        {getSortIcon('destination')}
                      </div>
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('phone')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Phone Number</span>
                        {getSortIcon('phone')}
                      </div>
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('trackingNumber')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Tracking Number</span>
                        {getSortIcon('trackingNumber')}
                      </div>
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Status</span>
                        {getSortIcon('status')}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedPackages.map((pkg) => (
                    <tr key={pkg.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div className="truncate">
                          {formatDate(pkg.updatedAt || pkg.createdAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="truncate font-medium text-gray-900" title={pkg.recipient || 'N/A'}>
                          {pkg.recipient || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="truncate text-gray-700" title={pkg.destination || 'N/A'}>
                          {pkg.destination || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="truncate text-gray-700">
                          {pkg.phone || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="truncate font-mono text-sm text-gray-600" title={pkg.trackingNumber || 'No Tracking #'}>
                          {pkg.trackingNumber || 'No Tracking #'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusStyle(pkg.status)}`}>
                          {formatStatus(pkg.status)}
                        </span>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <Link to={`/package/${pkg.id}`} className="text-indigo-600 hover:text-indigo-900 mr-4">
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Package count info */}
            <div className="px-6 py-3 bg-gray-50 text-xs text-gray-500 border-t border-gray-200">
              Showing {paginatedPackages.length} of {filteredPackages.length} packages
              {timeFilter !== 'all' && ` for the ${timeFilter} time period`}
            </div>
          </div>

          {/* Mobile Card View */}
          <div className={styles.mobileCardView}>
            {paginatedPackages.map((pkg) => (
              <div key={pkg.id} className={styles.packageCard}>
                {/* Card Header */}
                <div className={styles.cardHeader}>
                  <div className={styles.cardRecipient}>
                    <h3 className={styles.recipientName}>
                      {pkg.recipient || 'N/A'}
                    </h3>
                    <div className={styles.mobileDate}>
                      {formatDate(pkg.updatedAt || pkg.createdAt)}
                    </div>
                  </div>
                  <div className={styles.cardStatus}>
                    <span className={`${styles.statusBadge} ${getStatusStyle(pkg.status)}`}>
                      {formatStatus(pkg.status)}
                    </span>
                  </div>
                </div>

                {/* Card Body */}
                <div className={styles.cardBody}>
                  <div className={styles.cardRow}>
                    <span className={styles.cardLabel}>Tracking:</span>
                    <span className={styles.cardValueMono}>
                      {pkg.trackingNumber || 'No Tracking #'}
                    </span>
                  </div>

                  <div className={styles.cardRow}>
                    <span className={styles.cardLabel}>Details:</span>
                    <span className={styles.cardValue}>
                      {pkg.details || 'N/A'}
                    </span>
                  </div>
                  
                  <div className={styles.cardRow}>
                    <span className={styles.cardLabel}>Destination:</span>
                    <span className={styles.cardValue}>
                      {pkg.destination || 'N/A'}
                    </span>
                  </div>
                  
                  <div className={styles.cardRow}>
                    <span className={styles.cardLabel}>Phone:</span>
                    <span className={styles.cardValue}>
                      {pkg.phone || 'N/A'}
                    </span>
                  </div>
                </div>

                {/* Description Section - Separate and Prominent */}
                {pkg.description && (
                  <div className={styles.descriptionSection}>
                    <div className={styles.descriptionLabel}>Description:</div>
                    <div className={styles.descriptionText}>
                      {pkg.description}
                    </div>
                  </div>
                )}

                {/* Card Actions */}
                <div className={styles.cardActions}>
                  <Link 
                    to={`/package/${pkg.id}`} 
                    className="inline-flex items-center justify-center w-full px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            ))}
            
            {/* Mobile Package Count */}
            <div className="bg-white rounded-lg shadow p-3 text-center">
              <p className="text-xs text-gray-500">
                Showing {paginatedPackages.length} of {filteredPackages.length} packages
                {timeFilter !== 'all' && ` (${timeFilter})`}
              </p>
            </div>
          </div>
        </>
      )}

      {/* Back button - Mobile optimized */}
      <div className="mt-6 md:mt-8">
        <Link 
          to="/dashboard" 
          className="inline-flex items-center gap-2 text-indigo-600 font-semibold hover:text-indigo-800 transition-colors group"
        >
          <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span className="text-sm md:text-base">Back to Dashboard</span>
        </Link>
      </div>
    </div>
  );
}